import React, { useCallback, useEffect, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"
import i18n from "@/i18n"

const STYLES = {
  panel: "mt-5 overflow-hidden rounded-[10px] bg-[#fafafa] px-5 py-5 transition-[max-height] duration-300 ease-out",
  panelCollapsed: "max-h-[132px]",
  panelExpanded: "max-h-none",
  markdown:
    "prose prose-sm max-w-none text-[12px] leading-[18px] text-[#5f6774] prose-p:my-0 prose-ul:my-0 prose-li:my-0 prose-strong:text-[#444] prose-strong:font-semibold",
  toggle: "mt-4 cursor-pointer border-0 bg-transparent p-0 text-[14px] font-medium leading-[20px] text-[#2da0fe]",
} as const

const COLLAPSED_HEIGHT = 132

type BookingPolicyPanelProps = {
  content: string
}

const BookingPolicyPanel: React.FC<BookingPolicyPanelProps> = ({ content }) => {
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const checkOverflow = useCallback(() => {
    if (contentRef.current) {
      setOverflows(contentRef.current.scrollHeight > COLLAPSED_HEIGHT)
    }
  }, [])

  useEffect(() => {
    checkOverflow()
  }, [content, checkOverflow])

  useEffect(() => {
    const el = contentRef.current
    if (!el || typeof ResizeObserver === "undefined") return

    const observer = new ResizeObserver(checkOverflow)
    observer.observe(el)
    return () => observer.disconnect()
  }, [checkOverflow])

  return (
    <>
      <div
        ref={contentRef}
        className={`${STYLES.panel} ${expanded || !overflows ? STYLES.panelExpanded : STYLES.panelCollapsed}`}
        data-testid="settings-booking-policy-details-panel"
      >
        <div className={STYLES.markdown}>
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>
      </div>

      {overflows && (
        <button
          type="button"
          className={STYLES.toggle}
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          data-testid="settings-booking-policy-toggle"
        >
          {expanded ? `- ${i18n.t("app.booking_policy.show_less")}` : `+ ${i18n.t("app.booking_policy.show_more")}`}
        </button>
      )}
    </>
  )
}

export default BookingPolicyPanel
